// FloorHeightAnalyzer.jsx
// Updated version with environment variable support
import React, { useState } from 'react';
import axios from 'axios';
import './FloorHeightAnalyzer.css';

const FloorHeightAnalyzer = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  // Get API URL from environment variable (same as your existing setup)
  const PYTHON_API_URL = import.meta.env.VITE_PYTHON_API_URL || "http://localhost:8000";

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile(selectedFile);
      setError(null);
    } else {
      setError('Please select a valid PDF file');
      setFile(null);
    }
  };

  // Handle drag events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Handle drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.type === 'application/pdf') {
        setFile(droppedFile);
        setError(null);
      } else {
        setError('Please drop a valid PDF file');
      }
    }
  };

  // Submit file for analysis
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!file) {
      setError('Please select a PDF file first');
      return;
    }

    setLoading(true);
    setError(null);
    setResults(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      // Using your environment variable for API URL
      const response = await axios.post(
        `${PYTHON_API_URL}/api/pdf/analyze-floor-heights`,
        formData,
        {
          // headers: {
          //   'Content-Type': 'multipart/form-data',
          // },
          credentials: "include",
        }
      );

      setResults(response.data);
      setLoading(false);
    } catch (err) {
      setError(
        err.response?.data?.detail || 
        'Error analyzing PDF. Please try again.'
      );
      setLoading(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFile(null);
    setResults(null);
    setError(null);
  };

  return (
    <div className="floor-analyzer-container">
      <div className="analyzer-card">
        {/* Header */}
        <div className="analyzer-header">
          <h1>🏗️ Floor Height Calculator</h1>
          <p>Upload architectural section drawing PDF to analyze floor heights</p>
        </div>

        {/* Upload Area */}
        {!results && (
          <form onSubmit={handleSubmit} className="upload-form">
            <div
              className={`upload-area ${dragActive ? 'drag-active' : ''}`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => document.getElementById('fileInput').click()}
            >
              <div className="upload-icon">📄</div>
              <h3>
                {file ? file.name : 'Drop PDF here or click to browse'}
              </h3>
              <p>Supports architectural section drawings with FFL data</p>
              <input
                id="fileInput"
                type="file"
                accept=".pdf"
                onChange={handleFileChange}
                style={{ display: 'none' }}
              />
            </div>

            {file && (
              <div className="file-info">
                <span>✅ Selected: {file.name}</span>
                <button
                  type="button"
                  onClick={handleReset}
                  className="btn-secondary"
                >
                  Change File
                </button>
              </div>
            )}

            <button
              type="submit"
              className="btn-primary"
              disabled={!file || loading}
            >
              {loading ? (
                <>
                  <span className="spinner"></span>
                  Analyzing...
                </>
              ) : (
                'Analyze PDF'
              )}
            </button>
          </form>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message">
            <strong>⚠️ Error:</strong> {error}
          </div>
        )}

        {/* Results Display */}
        {results && (
          <div className="results-container">
            <div className="results-header">
              <h2>📊 Analysis Results</h2>
              <button onClick={handleReset} className="btn-secondary">
                Analyze Another PDF
              </button>
            </div>

            {/* Floor Levels */}
            <div className="section">
              <h3>🏢 Floor Levels ({results.total_levels})</h3>
              <div className="levels-grid">
                {results.levels.map((level, index) => (
                  <div key={index} className="level-card">
                    <h4>{level.name}</h4>
                    <p className="elevation">
                      FFL: <strong>{level.elevation_str}</strong> meters
                    </p>
                  </div>
                ))}
              </div>
            </div>

            {/* Floor Heights */}
            {results.heights.length > 0 && (
              <div className="section heights-section">
                <h3>📏 Floor Heights (Differences)</h3>
                <div className="heights-grid">
                  {results.heights.map((height, index) => (
                    <div key={index} className="height-card">
                      <div className="height-labels">
                        <span className="from-label">{height.from_level}</span>
                        <span className="arrow">→</span>
                        <span className="to-label">{height.to_level}</span>
                      </div>
                      <div className="height-value">
                        {height.height_meters.toFixed(3)} m
                      </div>
                      <div className="height-mm">
                        ({height.height_mm.toFixed(0)} mm)
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Summary */}
            <div className="summary-card">
              <p>
                ✅ {results.message || `Found ${results.total_levels} floor levels`}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FloorHeightAnalyzer;
